import { useEffect, useRef, useState } from "react";
import { Check, Download, Shield, Zap, Loader2 } from "lucide-react";


const ProgressStepper = ({ onComplete }) => {

    const [currentStep, setCurrentStep] = useState(0);
    const [progress, setProgress] = useState(0);
    const onCompleteRef = useRef(onComplete);
    useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);


    const steps = [
        {
            id: 1,
            title: "Verifying your connection",
            description: "Checking your device and network...",
            icon: Shield,
            duration: 4000,
        },
        {
            id: 2,
            title: "Preparing Download",
            description: "Securing your file and setting up access...",
            icon: Zap,
            duration: 4000,
        },
    ];

    useEffect(() => {
        if (currentStep >= steps.length) {
            setTimeout(() => onCompleteRef.current(), 500);
            return;
        }

        const step = steps[currentStep];
        const startTime = Date.now();

        const interval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const newProgress = Math.min((elapsed / step.duration) * 100, 100);
            setProgress(newProgress);

            if (newProgress >= 100) {
                clearInterval(interval);
                setTimeout(() => {
                    setCurrentStep(currentStep + 1);
                    setProgress(0);
                }, 300);
            }
        }, 50);

        return () => clearInterval(interval);
    }, [currentStep]);


    return (
        <div className="mt-[16px]  ">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] -z-10"></div>

            <div className="max-w-2xl w-full">
                <div className="bg-white rounded-3xl shadow-2xl p-6 border border-blue-200">
                    {/* Header */}
                    <div className="text-center mb-[16px]">
                        <div className="inline-flex items-center justify-center w-[52px] h-[52px] rounded-xl shadow-xl mb-[8px] animate-pulse" style={{ background: "linear-gradient(135deg, #01497c, #468faf)" }}>
                            <Download className="w-[26px] h-[26px] text-white" />
                        </div>
                        <h2 className="text-gray-900 mb-[4px] text-[22px] semiBold">Preparing Your Download</h2>
                        <p className="text-gray-500 text-[14px] medium">Please wait while we set everything up...</p>
                    </div>

                    {/* Steps */}
                    <div className="space-y-5 mb-6">
                        {steps.map((step, index) => {
                            const Icon = step.icon;
                            const isActive = index === currentStep;
                            const isCompleted = index < currentStep;

                            return (
                                <div
                                    key={step.id}
                                    className={`flex items-start gap-4 transition-all duration-500 ${isActive ? 'scale-105' : 'scale-100'}`}
                                >
                                    {/* Step Icon */}
                                    <div className="flex-shrink-0">
                                        <div
                                            className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-500 ${
                                                isCompleted
                                                    ? 'bg-green-500 shadow-lg shadow-green-200'
                                                    : isActive
                                                        ? 'shadow-lg animate-pulse'
                                                        : 'bg-gray-100'
                                            }`}
                                            style={isActive ? {
                                                background: "linear-gradient(135deg, #01497c, #468faf)",
                                                boxShadow: "0 10px 15px -3px rgba(70, 143, 175, 0.3)"
                                            } : undefined}
                                        >
                                            {isCompleted ? (
                                                <Check className="w-6 h-6 text-white" />
                                            ) : isActive ? (
                                                <Loader2 className="w-6 h-6 text-white animate-spin" />
                                            ) : (
                                                <Icon className="w-6 h-6 text-gray-400" />
                                            )}
                                        </div>
                                    </div>

                                    {/* Step Content */}
                                    <div className="flex-1 min-w-0 pt-1">
                                        <div className={`semiBold text-[16px] mb-0.5 transition-colors ${
                                            isActive || isCompleted ? 'text-gray-900' : 'text-gray-400'
                                        }`}>
                                            {step.title}
                                        </div>
                                        <div className={`medium text-[13px] transition-colors ${
                                            isActive ? 'text-gray-500' : 'text-gray-300'
                                        }`}>
                                            {step.description}
                                        </div>
                                        {isActive && (
                                            <div className="w-full bg-gray-200 rounded-full h-2 mt-2 overflow-hidden">
                                                <div
                                                    className="h-2 rounded-full transition-all duration-300"
                                                    style={{
                                                        width: `${progress}%`,
                                                        background: "linear-gradient(135deg, #01497c, #468faf)"
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Overall Progress */}
                    <div className="rounded-2xl p-4 border" style={{
                        background: "linear-gradient(90deg, rgba(70, 143, 175, 0.1), rgba(1, 73, 124, 0.1))",
                        borderColor: "#468faf"
                    }}>
                        <div className="flex items-center justify-between mb-2">
                            <span className="medium text-[14px] text-gray-600">Overall Progress</span>
                            <span className="semiBold text-[14px]" style={{ color: "#01497c" }}>
                                {currentStep} / {steps.length} steps
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                            <div
                                className="h-2 rounded-full transition-all duration-300"
                                style={{
                                    width: `${(currentStep / steps.length) * 100}%`,
                                    background: "linear-gradient(135deg, #01497c, #468faf)"
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProgressStepper